import { type Specialty } from '@/hooks/query/useQuerySpecialties';
import Fuse from 'fuse.js';
import { useMemo } from 'react';

interface TrieNode {
  children: Map<string, TrieNode>;
  isEndOfWord: boolean;
  specialty: Specialty | null;
}

function createNode(): TrieNode {
  return {
    children: new Map(),
    isEndOfWord: false,
    specialty: null,
  };
}

function getAcronym(text: string): string {
  return text
    .split(/[\s-]+/)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
}

function createSpecialtySearch(specialties: Specialty[]) {
  const root = createNode();
  const specialtyMap = new Map<string, Specialty>();

  const fuse = new Fuse(specialties, {
    keys: ['name', 'localizedName', 'group.name'],
    threshold: 0.4,
    distance: 100,
    includeScore: true,
    ignoreLocation: true,
    minMatchCharLength: 2,
  });

  function addToSpecialtyMap(specialty: Specialty) {
    for (const label of [specialty.name, specialty.localizedName]) {
      specialtyMap.set(label.toLowerCase(), specialty);

      const acronym = getAcronym(label);
      if (acronym.length > 1) {
        specialtyMap.set(acronym.toLowerCase(), specialty);
      }
    }

    // `name` is canonical English, so this alias now works in every display language.
    if (specialty.name.toLowerCase().includes('general practitioner')) {
      specialtyMap.set('gp', specialty);
    }
    // Add other common abbreviations as needed
  }

  function insertString(searchString: string, specialty: Specialty) {
    if (!searchString) return;
    let current = root;
    for (const char of searchString) {
      if (!current.children.has(char)) {
        current.children.set(char, createNode());
      }
      current = current.children.get(char)!;
    }
    current.isEndOfWord = true;
    current.specialty = specialty;
  }

  function insert(specialty: Specialty) {
    for (const label of [specialty.name, specialty.localizedName]) {
      insertString(label.toLowerCase(), specialty);
      insertString(getAcronym(label).toLowerCase(), specialty);
    }
  }

  function collectWords(node: TrieNode, matches: Specialty[]) {
    if (node.isEndOfWord && node.specialty) {
      matches.push(node.specialty);
    }

    for (const childNode of node.children.values()) {
      collectWords(childNode, matches);
    }
  }

  function findPrefixMatches(prefix: string): Specialty[] {
    const matches: Specialty[] = [];
    let current = root;
    prefix = prefix.toLowerCase();

    const directMatch = specialtyMap.get(prefix);
    if (directMatch) {
      matches.push(directMatch);
    }

    for (const char of prefix) {
      if (!current.children.has(char)) {
        return matches;
      }
      current = current.children.get(char)!;
    }

    collectWords(current, matches);
    return matches;
  }

  function search(query: string): Specialty[] {
    if (!query) return [];
    query = query.toLowerCase();

    const prefixMatches = findPrefixMatches(query);
    const fuzzyResults = fuse.search(query);
    const fuzzyMatches = fuzzyResults
      .filter((result) => result.score! < 0.4)
      .map((result) => result.item);

    const combined = [...new Set([...prefixMatches, ...fuzzyMatches])];

    return combined.sort((a, b) => {
      const aName = a.localizedName.toLowerCase();
      const bName = b.localizedName.toLowerCase();
      const aAcronym = getAcronym(a.localizedName).toLowerCase();
      const bAcronym = getAcronym(b.localizedName).toLowerCase();

      if (aName.startsWith(query) && !bName.startsWith(query)) return -1;
      if (!aName.startsWith(query) && bName.startsWith(query)) return 1;

      // Acronym matches second
      if (aAcronym === query && bAcronym !== query) return -1;
      if (aAcronym !== query && bAcronym === query) return 1;

      return aName.length - bName.length;
    });
  }

  specialties.forEach((specialty) => {
    insert(specialty);
    addToSpecialtyMap(specialty);
  });

  return { search };
}

export function useSpecialtySearch(specialties: Specialty[] | undefined) {
  const searchEngine = useMemo(() => {
    if (!specialties) return null;
    return createSpecialtySearch(specialties);
  }, [specialties]);

  const search = useMemo(
    () => (query: string) => {
      if (!searchEngine) return [];
      return searchEngine.search(query);
    },
    [searchEngine]
  );

  return {
    search,
    isReady: !!searchEngine,
  };
}
